import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { Loader2, KeyRound, ArrowLeft, AlertCircle } from 'lucide-react';
import { Button, Input, Label, Alert, AlertDescription, toast } from '@repo/ui';

const otpSchema = z.object({ otp: z.string().min(4, 'validation.required') });

type OtpFormValues = z.infer<typeof otpSchema>;

interface OtpStepProps {
  email: string;
  onBack: () => void;
  onSuccess: (otp: string) => void;
}

export const OtpStep: React.FC<OtpStepProps> = ({ email, onBack, onSuccess }) => {
  const { t } = useTranslation();
  const { forgotPassword, verifyOtp } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(120);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OtpFormValues>({
    // @ts-ignore
    resolver: async (values) => {
      const result = await otpSchema.safeParseAsync(values);
      if (result.success) return { values: result.data, errors: {} };
      const errors = result.error.issues.reduce((acc: any, issue) => {
        acc[issue.path[0] as string] = { type: issue.code, message: issue.message };
        return acc;
      }, {});
      return { values: {}, errors };
    },
    defaultValues: { otp: '' },
    mode: 'onChange',
  });

  const onSubmit = async (data: OtpFormValues) => {
    setError(null);
    try {
      await verifyOtp({ email, otp: data.otp });
      onSuccess(data.otp);
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.invalidOtp'));
    }
  };

  const handleResendOtp = useCallback(async () => {
    if (resendCooldown > 0 || isResending) return;
    setIsResending(true);
    setError(null);
    try {
      await forgotPassword(email);
      setResendCooldown(120);
      toast.success(t('auth.otpResent'));
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.failedToSendOtp'));
    } finally {
      setIsResending(false);
    }
  }, [email, forgotPassword, isResending, resendCooldown, t]);

  return (
    <div className="w-full">
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-8 pl-0 hover:bg-transparent hover:text-foreground text-muted-foreground cursor-pointer"
      >
        <ArrowLeft size={18} className="ltr:mr-2 rtl:ml-2 rtl:rotate-180" />
        {t('auth.back')}
      </Button>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <Label htmlFor="otp" className="mb-1.5 block">
            {t('auth.verificationCode')}
          </Label>
          <Input
            {...register('otp')}
            id="otp"
            type="text"
            placeholder={t('auth.enterOtpPlaceholder')}
            icon={<KeyRound className="h-4 w-4" />}
            error={!!errors.otp}
            className="h-11"
          />
          {errors.otp && (
            <div className="flex items-center gap-2 text-red-500 mt-1.5 pl-1 fade-in slide-in-from-top-1 duration-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm font-medium">{t(errors.otp.message as string)}</p>
            </div>
          )}
        </div>

        {error && (
          <Alert variant="error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full p bg-[oklch(0.34_0.17_262.91)] hover:bg-[oklch(0.34_0.17_262.91)]/90 text-white shadow-lg shadow-[oklch(0.34_0.17_262.91)]/20 -mt-3 cursor-pointer"
          size="sm"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mx-2" />
              {t('auth.verifying')}
            </>
          ) : (
            t('auth.verifyCode')
          )}
        </Button>

        <div className="text-center pt-2">
          <button
            type="button"
            onClick={handleResendOtp}
            disabled={resendCooldown > 0 || isResending}
            className={`text-sm font-semibold transition-colors ${
              resendCooldown > 0 || isResending
                ? 'text-muted-foreground cursor-not-allowed'
                : 'text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 cursor-pointer'
            }`}
          >
            {resendCooldown > 0
              ? t('auth.resendAvailableIn', { time: formatTime(resendCooldown) })
              : isResending
                ? t('auth.sending')
                : t('auth.resendCode')}
          </button>
        </div>
      </form>
    </div>
  );
};
